// Body metric calculators. Pure functions — no React, no Firestore.

export function calculateAge(dob) {
  if (!dob) return 0;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

export function calculateBMR(profile) {
  if (!profile.currentWeight || !profile.height || !profile.dateOfBirth) return 0;
  const weightKg = parseFloat(profile.currentWeight) * 0.453592;
  const heightCm = parseFloat(profile.height) * 2.54;
  const age = calculateAge(profile.dateOfBirth);
  if (profile.gender === 'Male') {
    return 10 * weightKg + 6.25 * heightCm - 5 * age + 5;
  } else {
    return 10 * weightKg + 6.25 * heightCm - 5 * age - 161;
  }
}

export function calculateBMI(profile) {
  if (!profile.currentWeight || !profile.height) return 0;
  const weightLbs = parseFloat(profile.currentWeight);
  const heightInches = parseFloat(profile.height);
  if (isNaN(weightLbs) || isNaN(heightInches) || heightInches <= 0) return 0;
  return (weightLbs * 703) / (heightInches * heightInches);
}

export function calculateBMIForRecord(weight, height) {
  if (!weight || !height) return null;
  const weightLbs = parseFloat(weight);
  const heightInches = parseFloat(height);
  if (isNaN(weightLbs) || isNaN(heightInches) || heightInches <= 0) return null;
  return (weightLbs * 703) / (heightInches * heightInches);
}

export function calculateFFMI(profile) {
  const bf = parseFloat(profile.bodyFatPercentage);
  if (!profile.currentWeight || !profile.height || !bf || isNaN(bf) || bf <= 0 || bf >= 100) return null;
  const weightKg = parseFloat(profile.currentWeight) / 2.205;
  const heightMeters = parseFloat(profile.height) * 0.0254;
  if (isNaN(weightKg) || isNaN(heightMeters) || heightMeters <= 0) return null;
  const leanMassKg = weightKg * (1 - bf / 100);
  const ffmi = leanMassKg / (heightMeters * heightMeters);
  const normalizedFFMI = ffmi + 6.1 * (1.8 - heightMeters);
  return normalizedFFMI;
}

export function calculateTargetGoals(profile) {
  const currentWeight = parseFloat(profile.currentWeight);
  const targetWeight = parseFloat(profile.targetWeight);
  if (!currentWeight || !targetWeight) {
    return currentWeight ? null : 'Enter your current weight to see goals.';
  }

  const weightDiff = currentWeight - targetWeight;
  const totalCalories = weightDiff * 3500;
  const isLoss = weightDiff > 0;
  const action = isLoss ? 'deficit' : 'surplus';

  if (weightDiff === 0) return '_You\'re at your target weight!_';

  if (profile.targetDate) {
    const today = new Date();
    const targetDate = new Date(profile.targetDate);
    const days = Math.max(1, Math.round((targetDate - today) / (1000 * 60 * 60 * 24)));
    let dailyCalories = Math.abs(totalCalories) / days;
    const weeklyCalories = dailyCalories * 7;

    if (days < 7) {
      return `Goal too close—needs over 1000 cal/day (${dailyCalories.toFixed(0)} cal/day). Extend your target date.`;
    }
    if (dailyCalories > 1000) {
      dailyCalories = 1000;
      return `To reach ${targetWeight} lbs by ${targetDate.toLocaleDateString()}, you need a ${action} capped at ${dailyCalories.toFixed(0)} cal/day (${(dailyCalories * 7).toFixed(0)} cal/week). This is the safe max—consider a later date.`;
    }
    return `To reach ${targetWeight} lbs by ${targetDate.toLocaleDateString()}, you need a ${action} of ${dailyCalories.toFixed(0)} cal/day (${weeklyCalories.toFixed(0)} cal/week).`;
  } else {
    const absCalories = Math.abs(totalCalories);
    const scenarios = [
      { deficit: 250, days: absCalories / 250 },
      { deficit: 500, days: absCalories / 500 },
      { deficit: 750, days: absCalories / 750 }
    ];
    const months = scenarios.map(s => (s.days / 30).toFixed(1));
    return `To ${isLoss ? 'lose' : 'gain'} ${Math.abs(weightDiff)} lbs (${absCalories.toFixed(0)} cal): 250 cal/day = ${months[0]} months, 500 cal/day = ${months[1]} months, 750 cal/day = ${months[2]} months.`;
  }
}
